import type { LugarDeComercia } from '../types/comercia.types';
import { normalizar } from './coincidencia';

/**
 * Lo que hace falta para mostrar un lugar del tarifario como en la maqueta:
 * «CN China · Shenzhen · Puerto de Yantian», y dónde dibujarlo en el mapa.
 */

const NOMBRES_DE_PAIS = (() => {
  try {
    return new Intl.DisplayNames(['es'], { type: 'region' });
  } catch {
    return null;
  }
})();

/** «CN» → «China». */
export function nombreDePais(codigo: string | null): string {
  if (!codigo) return 'Sin país';
  try {
    return NOMBRES_DE_PAIS?.of(codigo.toUpperCase()) ?? codigo;
  } catch {
    return codigo;
  }
}

/**
 * La ciudad de un lugar. El tarifario de ABCD trae puertos como «Yantian
 * (Shenzhen)»: la ciudad es la de los paréntesis y el puerto, lo de afuera.
 */
export function ciudadDe(l: LugarDeComercia): string {
  const m = /^(.+?)\s*\((.+)\)$/.exec(l.nombre);
  if (m) return m[2].trim();
  return l.ciudad || l.nombre;
}

/** El nombre del punto en sí: «Yantian» para «Yantian (Shenzhen)». */
export function nombreDelPunto(l: LugarDeComercia): string {
  const m = /^(.+?)\s*\((.+)\)$/.exec(l.nombre);
  return m ? m[1].trim() : l.nombre;
}

/** «Puerto de Yantian», «Aeropuerto de Viru Viru», «Terminal Santa Cruz». */
export function rotuloDelPunto(l: LugarDeComercia): string {
  const nombre = nombreDelPunto(l);
  if (l.tipo === 'SEAPORT') return `Puerto de ${nombre}`;
  if (l.tipo === 'AIRPORT') return `Aeropuerto de ${nombre}`;
  if (l.tipo === 'INLAND_TERMINAL') return `Terminal ${nombre}`;
  return nombre;
}

/** Cómo se sale o se llega a un punto: barco, avión o camión. */
export function modoDelPunto(l: LugarDeComercia): 'SEA' | 'AIR' | 'LAND' {
  if (l.tipo === 'AIRPORT') return 'AIR';
  if (l.tipo === 'SEAPORT') return 'SEA';
  return 'LAND';
}

/** Coordenadas conocidas de los puertos y ciudades del tarifario, por nombre. */
const COORDENADAS: Record<string, [number, number]> = {
  // China y alrededores
  shanghai: [121.47, 31.23],
  shenzhen: [114.06, 22.54],
  yantian: [114.27, 22.57],
  shekou: [113.92, 22.48],
  nansha: [113.6, 22.75],
  ningbo: [121.55, 29.87],
  qingdao: [120.38, 36.07],
  'hong kong': [114.17, 22.32],
  dalian: [121.61, 38.91],
  fuzhou: [119.3, 26.08],
  lianyungang: [119.22, 34.6],
  qinzhou: [108.62, 21.95],
  foshan: [113.12, 23.02],
  'sanshui foshan': [112.9, 23.16],
  shantou: [116.68, 23.35],
  tianjin: [117.2, 39.08],
  xingang: [117.75, 38.98],
  xiamen: [118.09, 24.48],
  guangzhou: [113.26, 23.13],
  busan: [129.04, 35.1],
  tokyo: [139.8, 35.65],
  yokohama: [139.64, 35.44],
  singapore: [103.82, 1.26],
  // Chile y Perú
  arica: [-70.31, -18.48],
  iquique: [-70.15, -20.21],
  antofagasta: [-70.4, -23.65],
  valparaiso: [-71.63, -33.05],
  'san antonio': [-71.62, -33.59],
  matarani: [-72.1, -17.0],
  callao: [-77.13, -12.05],
  // Bolivia
  'santa cruz': [-63.18, -17.78],
  'santa cruz de la sierra': [-63.18, -17.78],
  'la paz': [-68.15, -16.5],
  'el alto': [-68.19, -16.51],
  cochabamba: [-66.16, -17.39],
  oruro: [-67.11, -17.97],
  sucre: [-65.26, -19.04],
  potosi: [-65.75, -19.58],
  tarija: [-64.73, -21.53],
  // Otros puertos frecuentes
  santos: [-46.33, -23.96],
  'buenos aires': [-58.37, -34.6],
  miami: [-80.19, 25.76],
  'los angeles': [-118.27, 33.74],
  houston: [-95.37, 29.76],
  'new york': [-74.0, 40.68],
  rotterdam: [4.47, 51.9],
  hamburg: [9.97, 53.55],
  hamburgo: [9.97, 53.55],
  antwerp: [4.34, 51.3],
  amberes: [4.34, 51.3],
};

/** El centro aproximado de un país, cuando el lugar no está en la tabla. */
const CENTRO_DE_PAIS: Record<string, [number, number]> = {
  CN: [114, 30],
  HK: [114.17, 22.32],
  CL: [-70.6, -23],
  BO: [-64.7, -16.7],
  PE: [-76, -10],
  BR: [-47, -15],
  AR: [-64, -34],
  US: [-95, 37],
  DE: [10, 51],
  ES: [-3.7, 40],
  IT: [12.5, 42],
  JP: [139, 36],
  KR: [127.8, 36],
  IN: [78, 21],
  TW: [121, 23.7],
  VN: [106.6, 10.8],
  MX: [-99, 19.4],
  PA: [-79.5, 9],
};

/** [longitud, latitud] del lugar, o null si no hay forma de ubicarlo. */
export function coordenadasDe(l: LugarDeComercia): [number, number] | null {
  if (l.latitud != null && l.longitud != null) return [l.longitud, l.latitud];
  for (const clave of [l.nombre, nombreDelPunto(l), ciudadDe(l), l.ciudad]) {
    const c = COORDENADAS[normalizar(clave ?? '').trim()];
    if (c) return c;
  }
  return l.pais ? (CENTRO_DE_PAIS[l.pais.toUpperCase()] ?? null) : null;
}

/** La longitud del punto medio del arco más corto entre dos lugares (para centrar el mapa). */
export function longitudDelMedio(a: [number, number], b: [number, number]): number {
  const rad = Math.PI / 180;
  const [l1, f1] = [a[0] * rad, a[1] * rad];
  const [l2, f2] = [b[0] * rad, b[1] * rad];
  const bx = Math.cos(f2) * Math.cos(l2 - l1);
  const by = Math.cos(f2) * Math.sin(l2 - l1);
  const l3 = l1 + Math.atan2(by, Math.cos(f1) + bx);
  return ((l3 / rad + 540) % 360) - 180;
}
